import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import { Slideout } from '../slideout';
import { useTeamInfo } from '../../team-id-context';
import { useNotifications } from '../../notifications';
import { GET_IMPORT_TEMPLATE, IMPORT_TEAM_CONTACTS } from '../../api/contacts/importApi';
import Papa from 'papaparse';
import styles from './ImportContactsSlideout.module.scss';

interface ImportContactsSlideoutProps {
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'results';

interface ParsedContact {
  [key: string]: string;
}

interface FieldMapping {
  [csvColumn: string]: string; // Maps CSV column to system field name
}

interface ImportResults {
  total: number;
  successful: number;
  failed: number;
  duplicatesSkipped: number;
  failedRows: Array<{
    row: number;
    data: Record<string, string>;
    errors: string[];
  }>;
}

interface FieldDropdownProps {
  value: string;
  onChange: (value: string) => void;
  systemFields: string[];
  customFields: string[];
}

const FieldDropdown: React.FC<FieldDropdownProps> = ({ value, onChange, systemFields, customFields }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDisplayValue = () => {
    if (!value) return 'Do not import';
    return value.replace('_', ' ');
  };

  const handleSelect = (fieldValue: string) => {
    onChange(fieldValue);
    setIsOpen(false);
  };

  return (
    <div className={styles.customDropdown} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.dropdownTrigger} ${!value ? styles.placeholder : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{getDisplayValue()}</span>
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <div className={styles.dropdownOption + (value === '' ? ` ${styles.selected}` : '')} onClick={() => handleSelect('')}>
            <span className={styles.placeholder}>Do not import</span>
          </div>

          <div className={styles.dropdownGroup}>
            <div className={styles.dropdownGroupLabel}>Standard Fields</div>
            {systemFields.map((field) => (
              <div
                key={field}
                className={styles.dropdownOption + (value === field ? ` ${styles.selected}` : '')}
                onClick={() => handleSelect(field)}
              >
                {field.replace('_', ' ')}
              </div>
            ))}
          </div>

          {customFields.length > 0 && (
            <div className={styles.dropdownGroup}>
              <div className={styles.dropdownGroupLabel}>Custom Fields</div>
              {customFields.map((field) => (
                <div
                  key={field}
                  className={styles.dropdownOption + (value === field ? ` ${styles.selected}` : '')}
                  onClick={() => handleSelect(field)}
                >
                  {field}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ImportContactsSlideout: React.FC<ImportContactsSlideoutProps> = ({ onClose, onImportComplete }) => {
  const { teamId } = useTeamInfo();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [step, setStep] = useState<ImportStep>('upload');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [rawCsvData, setRawCsvData] = useState<ParsedContact[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({});
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch template headers
  const { data: templateData } = useQuery(GET_IMPORT_TEMPLATE, {
    variables: { teamId },
    skip: !teamId,
  });

  const [importContacts] = useMutation(IMPORT_TEAM_CONTACTS);

  const templateHeaders = templateData?.teamContactImportTemplate || [];

  // Get available system fields
  const systemFields = ['contact_type', 'name', 'email', 'phone', 'company', 'title', 'address'];
  const availableFields = [...systemFields, ...templateHeaders.filter((h: string) => !systemFields.includes(h))];

  // Smart field matching helper
  const smartMatchField = useCallback((csvColumn: string): string => {
    const normalized = csvColumn.toLowerCase().trim().replace(/[_\s-]/g, '');

    // Exact match
    if (availableFields.includes(csvColumn)) {
      return csvColumn;
    }

    // Fuzzy matching
    const fuzzyMatches: Record<string, string[]> = {
      'contact_type': ['contacttype', 'type', 'kind'],
      'name': ['fullname', 'contactname', 'companyname'],
      'email': ['emailaddress', 'mail'],
      'phone': ['phonenumber', 'telephone', 'tel', 'mobile'],
      'company': ['organization', 'org', 'business'],
      'title': ['jobtitle', 'position', 'role'],
      'address': ['location', 'streetaddress', 'addr'],
    };

    for (const [field, matches] of Object.entries(fuzzyMatches)) {
      if (matches.includes(normalized)) {
        return field;
      }
    }

    // Check if normalized matches any available field
    const match = availableFields.find((f: string) =>
      f.toLowerCase().replace(/[_\s-]/g, '') === normalized
    );

    return match || ''; // Empty string means "Do not import"
  }, [availableFields]);

  // Download template CSV
  const handleDownloadTemplate = useCallback(() => {
    // Helper to properly escape CSV values (wrap in quotes if contains comma, quote, or newline)
    const escapeCsvValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        // Escape double quotes by doubling them, then wrap in quotes
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV with headers and example rows
    const headers = templateHeaders;
    const rows = [];

    // Add header row
    rows.push(headers.map(escapeCsvValue).join(','));

    // Add example person contact
    const personExample = headers.map((header) => {
      switch (header) {
        case 'contact_type':
          return 'person';
        case 'name':
          return 'John Smith';
        case 'email':
          return 'john.smith@example.com';
        case 'phone':
          return '(555) 123-4567';
        case 'company':
          return 'Acme Corp';
        case 'title':
          return 'CEO';
        case 'address':
          return '123 Main St, New York, NY 10001';
        default:
          return ''; // Leave custom fields empty
      }
    });
    rows.push(personExample.map(escapeCsvValue).join(','));

    // Add example company contact
    const companyExample = headers.map((header) => {
      switch (header) {
        case 'contact_type':
          return 'company';
        case 'name':
          return 'Tech Solutions Inc';
        case 'email':
          return 'info@techsolutions.com';
        case 'phone':
          return '(555) 987-6543';
        case 'company':
          return ''; // Companies don't have a company field
        case 'title':
          return ''; // Companies don't have a title field
        case 'address':
          return '456 Corporate Blvd, San Francisco, CA 94102';
        default:
          return ''; // Leave custom fields empty
      }
    });
    rows.push(companyExample.map(escapeCsvValue).join(','));

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contact_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [templateHeaders]);

  // Handle file selection
  const handleFileSelect = useCallback(
    (file: File) => {
      if (!file.name.endsWith('.csv')) {
        notifyError({ message: 'Please upload a CSV file' });
        return;
      }

      setCsvFile(file);

      // Mac OS Roman character mapping for common accented characters (0x80-0xFF)
      const macRomanToUnicode: { [key: number]: string } = {
        0x80: 'Ä', 0x81: 'Å', 0x82: 'Ç', 0x83: 'É', 0x84: 'Ñ', 0x85: 'Ö', 0x86: 'Ü',
        0x87: 'á', 0x88: 'à', 0x89: 'â', 0x8a: 'ä', 0x8b: 'ã', 0x8c: 'å', 0x8d: 'ç',
        0x8e: 'é', 0x8f: 'è', 0x90: 'ê', 0x91: 'ë', 0x92: 'í', 0x93: 'ì', 0x94: 'î',
        0x95: 'ï', 0x96: 'ñ', 0x97: 'ó', 0x98: 'ò', 0x99: 'ô', 0x9a: 'ö', 0x9b: 'õ',
        0x9c: 'ú', 0x9d: 'ù', 0x9e: 'û', 0x9f: 'ü', 0xcb: 'À', 0xcc: 'Ã', 0xcd: 'Õ',
        0xe0: '†', 0xe1: '°', 0xe7: 'º', 0xe8: 'æ', 0xe9: 'ø',
      };

      const decodeMacRoman = (bytes: Uint8Array): string => {
        let result = '';
        for (let i = 0; i < bytes.length; i++) {
          const byte = bytes[i];
          if (byte < 0x80) {
            // ASCII range - use as is
            result += String.fromCharCode(byte);
          } else if (macRomanToUnicode[byte]) {
            // Mac Roman special character
            result += macRomanToUnicode[byte];
          } else {
            // Unmapped character - use as is (may not be perfect but better than nothing)
            result += String.fromCharCode(byte);
          }
        }
        return result;
      };

      // Read file with proper encoding detection
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);

        // Detect BOM (Byte Order Mark) first
        let text: string;

        // Check for UTF-8 BOM (EF BB BF)
        if (uint8Array.length >= 3 && uint8Array[0] === 0xef && uint8Array[1] === 0xbb && uint8Array[2] === 0xbf) {
          // UTF-8 with BOM - skip the BOM bytes
          const decoder = new TextDecoder('utf-8');
          text = decoder.decode(uint8Array.slice(3));
        }
        // Check for UTF-16 LE BOM (FF FE)
        else if (uint8Array.length >= 2 && uint8Array[0] === 0xff && uint8Array[1] === 0xfe) {
          const decoder = new TextDecoder('utf-16le');
          text = decoder.decode(uint8Array.slice(2));
        }
        // Check for UTF-16 BE BOM (FE FF)
        else if (uint8Array.length >= 2 && uint8Array[0] === 0xfe && uint8Array[1] === 0xff) {
          const decoder = new TextDecoder('utf-16be');
          text = decoder.decode(uint8Array.slice(2));
        }
        // No BOM - try to auto-detect
        else {
          // Try UTF-8 first to check for replacement characters
          const utf8Decoder = new TextDecoder('utf-8');
          const utf8Text = utf8Decoder.decode(uint8Array);
          const hasReplacementChars = utf8Text.includes('\uFFFD');

          if (hasReplacementChars) {
            // Check if this might be Mac Roman encoding by looking for bytes in 0x80-0x9F range
            // (These are control characters in Windows-1252 but printable in Mac Roman)
            const hasMacRomanChars = Array.from(uint8Array).some((byte) => byte >= 0x80 && byte <= 0x9f);

            if (hasMacRomanChars) {
              // Likely Mac Roman encoding
              text = decodeMacRoman(uint8Array);
            } else {
              // Try Windows-1252 for Excel exports
              try {
                const windows1252Decoder = new TextDecoder('windows-1252');
                text = windows1252Decoder.decode(uint8Array);
              } catch (error) {
                // Fallback to ISO-8859-1 (Latin-1)
                const isoDecoder = new TextDecoder('iso-8859-1');
                text = isoDecoder.decode(uint8Array);
              }
            }
          } else {
            // No replacement characters - safe to use UTF-8
            text = utf8Text;
          }
        }

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const contacts = results.data as ParsedContact[];

            if (contacts.length === 0) {
              notifyError({ message: 'CSV file is empty' });
              return;
            }

            if (contacts.length > 3000) {
              notifyError({ message: 'Maximum 3000 contacts per import. Please split your file.' });
              return;
            }

            // Extract CSV headers and store raw data
            const headers = Object.keys(contacts[0] || {});
            setCsvHeaders(headers);
            setRawCsvData(contacts);

            // Create smart field mapping
            const initialMapping: FieldMapping = {};
            headers.forEach((header) => {
              initialMapping[header] = smartMatchField(header);
            });
            setFieldMapping(initialMapping);

            // Go to mapping step
            setStep('mapping');
          },
          error: (error) => {
            notifyError({ message: `Failed to parse CSV: ${error.message}` });
          },
        });
      };

      reader.onerror = () => {
        notifyError({ message: 'Failed to read CSV file' });
      };

      // Read file as ArrayBuffer for proper encoding handling
      reader.readAsArrayBuffer(file);
    },
    [notifyError, smartMatchField]
  );

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  // Handle file input
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  // Trigger file input click
  const handleChooseFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle field mapping change
  const handleMappingChange = useCallback((csvColumn: string, targetField: string) => {
    setFieldMapping((prev) => ({
      ...prev,
      [csvColumn]: targetField,
    }));
  }, []);

  // Apply mapping and go to preview
  const handleContinueToPreview = useCallback(() => {
    // Transform raw data using field mapping
    const mappedData = rawCsvData.map((row) => {
      const mappedRow: ParsedContact = {};

      Object.entries(fieldMapping).forEach(([csvColumn, targetField]) => {
        if (targetField && targetField !== '') {
          // Only include if mapped to a field (not "Do not import")
          mappedRow[targetField] = row[csvColumn] || '';
        }
      });

      return mappedRow;
    });

    setParsedContacts(mappedData);
    setStep('preview');
  }, [rawCsvData, fieldMapping]);

  // Perform import
  const handleImport = useCallback(async () => {
    setStep('importing');

    try {
      const result = await importContacts({
        variables: {
          teamId,
          contactsData: parsedContacts,
        },
      });

      const data = result.data?.importTeamContacts;
      if (data) {
        setImportResults({
          total: data.total,
          successful: data.successful,
          failed: data.failed,
          duplicatesSkipped: data.duplicatesSkipped,
          failedRows: data.failedRows || [],
        });
        setStep('results');

        // Refresh contacts list
        await onImportComplete();

        // Show success notification after data refresh completes
        setTimeout(() => {
          notifySuccess({ message: `Successfully imported ${data.successful} contacts` });
        }, 100);
      }
    } catch (error: any) {
      notifyError({ message: error.message || 'Import failed' });
      setStep('preview');
    }
  }, [teamId, parsedContacts, importContacts, onImportComplete, notifySuccess, notifyError]);

  // Download failed rows CSV
  const handleDownloadFailedRows = useCallback(() => {
    if (!importResults || importResults.failedRows.length === 0) return;

    const headers = Object.keys(importResults.failedRows[0].data);
    const rows = importResults.failedRows.map((row) => {
      const values = headers.map((header) => row.data[header] || '');
      return values.join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'failed_contacts.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [importResults]);

  // Reset and start over
  const handleStartOver = useCallback(() => {
    setCsvFile(null);
    setRawCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setParsedContacts([]);
    setImportResults(null);
    setStep('upload');
  }, []);

  return (
    <Slideout
      isOpen={true}
      onClose={onClose}
      title="Import Contacts"
      width="medium"
      disableOutsideClick={true}
    >
      <div className={styles.slideoutContent}>
        {/* Upload Step */}
        {step === 'upload' && (
          <>
            <div
              className={`${styles.dropzone} ${isDragging ? styles.dragging : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className={styles.dropzoneContent}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <h3>Drag and drop your CSV file here</h3>
                <p>or</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileInput}
                  className={styles.fileInput}
                />
                <Button variant="primary" onClick={handleChooseFileClick}>
                  Choose File
                </Button>
                <p className={styles.hint}>Maximum 3,000 contacts per import</p>
              </div>
            </div>

            <div className={styles.templateLink}>
              <Button
                variant="link"
                className="text-primary font-weight-bold p-0"
                onClick={handleDownloadTemplate}
                disabled={!templateHeaders.length}
                style={{ textDecoration: 'none' }}
              >
                Download Template CSV
              </Button>
            </div>
          </>
        )}

        {/* Field Mapping Step */}
        {step === 'mapping' && (
          <>
            <div className={styles.mappingSection}>
              <h3>Map CSV Columns to Fields</h3>
              <p className={styles.mappingSummary}>
                Match your CSV columns to contact fields. We've auto-detected some matches.
              </p>

              <div className={styles.mappingTable}>
                <div className={styles.mappingHeader}>
                  <span>CSV Column</span>
                  <span>Maps To</span>
                </div>
                {csvHeaders.map((header) => (
                  <div key={header} className={styles.mappingRow}>
                    <div className={styles.csvColumn}>
                      <strong>{header}</strong>
                      <span className={styles.sampleValue}>
                        {rawCsvData[0]?.[header] ? `e.g., "${rawCsvData[0][header]}"` : ''}
                      </span>
                    </div>
                    <div className={styles.fieldSelect}>
                      <FieldDropdown
                        value={fieldMapping[header] || ''}
                        onChange={(value) => handleMappingChange(header, value)}
                        systemFields={systemFields}
                        customFields={templateHeaders.filter((h: string) => !systemFields.includes(h))}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.mappingActions}>
                <Button variant="link" onClick={handleStartOver}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleContinueToPreview}>
                  Continue to Preview
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Preview Step */}
        {step === 'preview' && (
          <>
            <div className={styles.previewSection}>
              <h3>Preview Import</h3>
              <p className={styles.previewSummary}>
                Ready to import <strong>{parsedContacts.length}</strong> contacts from{' '}
                <strong>{csvFile?.name}</strong>
              </p>

              <div className={styles.previewTable}>
                <table>
                  <thead>
                    <tr>
                      {Object.keys(parsedContacts[0] || {}).map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedContacts.slice(0, 5).map((contact, index) => (
                      <tr key={index}>
                        {Object.values(contact).map((value, i) => (
                          <td key={i}>{value}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedContacts.length > 5 && (
                  <p className={styles.previewMore}>... and {parsedContacts.length - 5} more contacts</p>
                )}
              </div>

              <div className={styles.previewActions}>
                <Button variant="link" onClick={handleStartOver}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleImport}>
                  Import Contacts
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Importing Step */}
        {step === 'importing' && (
          <div className={styles.importingSection}>
            <div className={styles.spinner} />
            <h3>Importing contacts...</h3>
            <p>Please wait while we process your contacts. This may take a moment.</p>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && importResults && (
          <div className={styles.resultsSection}>
            <h3>Import Complete</h3>

            <div className={styles.resultsSummary}>
              <div className={styles.resultItem}>
                <span className={styles.resultLabel}>Total Contacts:</span>
                <span className={styles.resultValue}>{importResults.total}</span>
              </div>
              <div className={`${styles.resultItem} ${styles.success}`}>
                <span className={styles.resultLabel}>Successfully Imported:</span>
                <span className={styles.resultValue}>{importResults.successful}</span>
              </div>
              {importResults.duplicatesSkipped > 0 && (
                <div className={`${styles.resultItem} ${styles.warning}`}>
                  <span className={styles.resultLabel}>Duplicates Skipped:</span>
                  <span className={styles.resultValue}>{importResults.duplicatesSkipped}</span>
                </div>
              )}
              {importResults.failed > 0 && (
                <div className={`${styles.resultItem} ${styles.error}`}>
                  <span className={styles.resultLabel}>Failed:</span>
                  <span className={styles.resultValue}>{importResults.failed}</span>
                </div>
              )}
            </div>

            {importResults.failedRows.length > 0 && (
              <div className={styles.failedRows}>
                <h4>{importResults.failed} {importResults.failed === 1 ? 'contact' : 'contacts'} could not be imported</h4>
                <p>Download the CSV below to see details and fix issues.</p>

                {/* Group errors by error message and show summary */}
                {(() => {
                  const errorCounts: Record<string, number> = {};
                  importResults.failedRows.forEach((row) => {
                    row.errors.forEach((error) => {
                      errorCounts[error] = (errorCounts[error] || 0) + 1;
                    });
                  });

                  const errorEntries = Object.entries(errorCounts).sort((a, b) => b[1] - a[1]);

                  return errorEntries.length > 0 ? (
                    <div className={styles.errorSummary}>
                      <p className={styles.errorSummaryTitle}>Common issues:</p>
                      {errorEntries.map(([error, count]) => (
                        <div key={error} className={styles.errorSummaryItem}>
                          <span className={styles.errorCount}>{count}×</span>
                          <span className={styles.errorText}>{error}</span>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}

                <Button variant="danger" onClick={handleDownloadFailedRows} className={styles.downloadBtn}>
                  Download Failed Contacts CSV
                </Button>
              </div>
            )}

            <div className={styles.resultsActions}>
              <Button variant="link" onClick={handleStartOver}>
                Import More Contacts
              </Button>
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </Slideout>
  );
};

export default ImportContactsSlideout;
